import BaseChart from './BaseChart';
import { CHART_ANIMATIONS } from '../config/animations';
import { DEFAULT_COLORS } from '../config/themes';
import { DEFAULT_LEGEND, LEGEND_POSITIONS } from '../config/legends';

/**
 * Bar chart implementation using VChart
 */
class BarChart extends BaseChart {
  /**
   * Get chart specification for bar chart
   * @returns {Object} Chart specification
   */
  getChartSpecification() {
    const commonConfig = this.getCommonConfig();
    const data = this.prepareData();
    
    // Determine if horizontal orientation is requested
    const isHorizontal = this.options.direction === 'horizontal';
    
    // Determine fields for x and y axes.
    // Use _resolveNameField() to auto-detect the category field from data
    // when no explicit xField is provided (handles LLM-chosen field names
    // like "product", "region", etc. instead of defaulting to "category").
    const detectedNameField = this._resolveNameField();
    const detectedValueField = this._resolveValueField();
    const baseXField = this.options.xField || (isHorizontal ? detectedValueField : detectedNameField);
    const yField = this.options.yField || (isHorizontal ? detectedNameField : detectedValueField);

    // Get series field for grouping if available
    // Check if data has 'group' field (for grouped bar charts)
    const hasGroupField = data[0]?.values?.some(item => item.group !== undefined);
    const hasIndividualColors = data[0]?.values?.some(item => item.color !== undefined);
    const seriesField = this.options.seriesField || (hasGroupField ? 'group' : (hasIndividualColors ? 'category' : null));

    // For grouped bars (side-by-side), xField must be an array with [category, group]
    // For stacked bars or single series, xField is just the category field
    const shouldBeGrouped = hasGroupField && this.options.stack !== true;
    const xField = shouldBeGrouped ? [baseXField, seriesField] : baseXField;
    
    // Extract colors from data if present
    let colorConfig = this.options.color || DEFAULT_COLORS;
    if (hasIndividualColors && data[0]?.values) {
      const colorMap = {};
      data[0].values.forEach(item => {
        if (item.color) {
          colorMap[item.category] = item.color;
        }
      });
      if (Object.keys(colorMap).length > 0) {
        colorConfig = colorMap;
      }
    }
    
    // Configure legends specifically for bar charts to show at the bottom
    // Only override when using different colors for bars
    const legendConfig = {
      ...DEFAULT_LEGEND,
      ...LEGEND_POSITIONS.BOTTOM,
      visible: this.options.legends?.visible !== false,
      item: {
        ...DEFAULT_LEGEND.item,
        name: (datum) => {
          // First try using seriesField value directly 
          if (datum && datum[seriesField]) {
            return datum[seriesField];
          }
          // Then try name field
          if (datum && datum.name) {
            return datum.name;
          }
          // If using xField as the name for the legend
          if (datum && datum[xField]) {
            return datum[xField];
          }
          return datum;
        }
      },
      ...this.options.legends
    };
    
    return {
      type: 'bar',
      data,
      xField: isHorizontal ? yField : xField,
      yField: isHorizontal ? xField : yField,
      seriesField,
      direction: this.options.direction || 'vertical',

      // Use color configuration (either from data or options)
      color: colorConfig,
      
      // Bar specific styling
      bar: {
        style: {
          cornerRadius: 4,
          maxWidth: this.options.maxBarWidth || 60,
          ...this.options.barStyle
        },
        state: {
          hover: {
            fillOpacity: 0.8,
            stroke: '#fff',
            lineWidth: 2
          },
          ...this.options.barState
        }
      },
      
      // Bar label configuration with improved styling
      label: {
        visible: this.options.showLabels !== false,
        position: this.options.labelPosition || 'top',
        style: {
          fontSize: 16,
          fontWeight: 'bold',
          fill: '#333333',
          stroke: '#ffffff',
          lineWidth: 3,
          lineJoin: 'round',
          textAlign: 'center',
          textBaseline: 'bottom',
          ...this.options.labelStyle
        },
        formatMethod: (text, datum) => {
          // Format the label if a formatter is provided, otherwise return the raw value
          if (this.options.labelFormatter) {
            return this.options.labelFormatter(text, datum);
          }
          // Format large numbers with commas
          const num = parseFloat(text);
          if (!isNaN(num)) {
            return num.toLocaleString();
          }
          return text;
        }
      },
      
      // Axes configuration
      axes: this.getAxesConfig(isHorizontal),
      
      // Crosshair for hover interactions
      crosshair: {
        xField: { visible: true },
        yField: { visible: false }
      },
      
      // Legend configuration
      legends: legendConfig,
      
      // Animation configuration specific to bar charts
      animationAppear: {
        ...CHART_ANIMATIONS.bar.appear,
        ...this.options.animationAppear
      },
      
      // Common configuration from BaseChart
      ...commonConfig
    };
  }
  
  /**
   * Prepare data for VChart format
   * @returns {Array} Formatted data for VChart
   */
  prepareData() {
    // Default implementation
    if (!Array.isArray(this.data)) {
      this.data = [this.data];
    }
    
    // Add _itemId field to each data item to serve as seriesField for coloring
    const formattedData = this.data.map((item, index) => {
      const meaningfulName = this.getMeaningfulItemName(item, index);
      return {
        ...item,
        _itemId: meaningfulName, // Use meaningful name as the itemId for seriesField
        name: meaningfulName // Use meaningful name for legend
      };
    });
    
    return [
      {
        id: 'data',
        values: formattedData
      }
    ];
  }
  
  /**
   * Get axes configuration
   * @param {boolean} isHorizontal Whether the chart is horizontal
   * @returns {Array} Axes configuration
   */
  getAxesConfig(isHorizontal) {
    // X-axis title from options
    const xAxisTitle = this.options.xAxis?.title?.text || this.options.xAxisTitle;
    
    // Y-axis title from options
    const yAxisTitle = this.options.yAxis?.title?.text || this.options.yAxisTitle;
    
    // Default axes configuration
    const axesConfig = [
      {
        orient: isHorizontal ? 'left' : 'bottom',
        type: 'band',
        label: {
          visible: true,
          style: {
            fontSize: 12,
            ...this.options.xAxisLabelStyle
          },
          formatMethod: (text) => {
            // Format the label if a formatter is provided
            if (this.options.xAxisLabelFormatter) {
              return this.options.xAxisLabelFormatter(text);
            }
            // If text is very long, truncate it
            if (text && text.length > 15) {
              return text.substring(0, 12) + '...';
            }
            return text;
          }
        },
        title: {
          visible: !!xAxisTitle,
          text: xAxisTitle || '',
          style: {
            fontSize: 14,
            fontWeight: 'bold',
            ...this.options.xAxisTitleStyle
          }
        },
        ...(this.options.xAxis || {})
      },
      {
        orient: isHorizontal ? 'bottom' : 'left',
        type: 'linear',
        label: {
          visible: true,
          style: {
            fontSize: 12,
            ...this.options.yAxisLabelStyle
          },
          formatMethod: (text) => {
            // Format the label if a formatter is provided
            if (this.options.yAxisLabelFormatter) {
              return this.options.yAxisLabelFormatter(text);
            }
            return text;
          }
        },
        title: {
          visible: !!yAxisTitle,
          text: yAxisTitle || '',
          style: {
            fontSize: 14,
            fontWeight: 'bold',
            ...this.options.yAxisTitleStyle
          }
        },
        grid: {
          visible: true,
          style: {
            stroke: '#E8E8E8',
            lineWidth: 1,
            lineDash: [0],
            ...this.options.gridStyle
          }
        },
        ...(this.options.yAxis || {})
      }
    ];
    
    return axesConfig;
  }
}

export default BarChart; 